import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Button, Row, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { rateJob, rateJobByDocumentId } from "../../../docuclipper/api";
import { ReduxState } from "../../../redux";
import { createAlert } from "../../../redux/reducers/Alerts";
import { closeModal } from "../../../redux/reducers/ModalManager";
import AlertBanner from "../AlertBanner/AlertBanner";
import ClipLoader from "react-spinners/ClipLoader";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import { formikTextArea } from "../../../utils/utils";
import { fetchJobRatings } from "src/redux/reducers/JobRatings";

const bankStatementIssueTypes = [
  { value: "wrong_start_balance", label: "Wrong Start Balance" },
  { value: "wrong_end_balance", label: "Wrong End Balance" },
  { value: "wrong_start_date", label: "Wrong Start Date" },
  { value: "wrong_end_date", label: "Wrong End Date" },
  { value: "missing_transaction", label: "Missing Transaction" },
  { value: "wrong_transaction_date", label: "Wrong Transaction Date" },
  { value: "wrong_transaction_amount", label: "Wrong Transaction Amount" },
  {
    value: "wrong_transaction_description",
    label: "Wrong Transaction Description",
  },
  { value: "wrong_transaction_balance", label: "Wrong Transaction Balance" },
  {
    value: "multiple_accounts",
    label: "Multiple Accounts Not Detected Correctly",
  },
  {
    value: "multiple_periods",
    label: "Multiple Periods Not Detected Correctly",
  },
  { value: "other", label: "Other" },
];

const invoiceIssueTypes = [
  { value: "wrong_vendor", label: "Wrong Vendor Name" },
  { value: "wrong_customer", label: "Wrong Customer / Bill-To" },
  { value: "wrong_invoice_number", label: "Wrong Invoice Number" },
  { value: "wrong_invoice_date", label: "Wrong Invoice Date" },
  { value: "wrong_due_date", label: "Wrong Due Date" },
  { value: "wrong_subtotal", label: "Wrong Subtotal" },
  { value: "wrong_tax", label: "Wrong Tax Amount" },
  { value: "wrong_total", label: "Wrong Total Amount" },
  { value: "wrong_currency", label: "Wrong Currency" },
  { value: "missing_line_item", label: "Missing Line Item" },
  { value: "wrong_line_item", label: "Wrong Line Item (description, qty, price)" },
  { value: "other", label: "Other" },
];

const formIssueTypes = [
  { value: "wrong_field_value", label: "Wrong Field Value" },
  { value: "missing_field", label: "Missing Field" },
  { value: "wrong_field_location", label: "Field Extracted From Wrong Location" },
  { value: "wrong_checkbox", label: "Wrong Checkbox State" },
  { value: "wrong_date", label: "Wrong Date" },
  { value: "wrong_amount", label: "Wrong Amount" },
  { value: "other", label: "Other" },
];

const genericIssueTypes = [{ value: "other", label: "Other" }];

type Props = {
  description: string;
  cta: string;
  docType: "bank" | "invoice" | "form" | "other";
};

type IssueFormValues = {
  pageNumber: string;
  issueType: string;
  details: string;
  issues: Array<{
    pageNumber: string;
    issueType: string;
    details: string;
  }>;
  touched?: {
    pageNumber: boolean;
    issueType: boolean;
    details: boolean;
  };
};

export const DescribeIssue = ({ description, cta, docType }: Props) => {
  const [loading, setLoading] = React.useState(false);
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedDocumentId } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );

  const { documentId } = useSelector(
    (state: ReduxState) => state.InvoiceManager
  );

  const dispatch = useDispatch();
  const initialValues: IssueFormValues = {
    pageNumber: "",
    issueType: "",
    details: "",
    issues: [],
  };

  const handleSubmit = (values: IssueFormValues, { resetForm }) => {
    setLoading(true);
    const formattedIssues = values.issues.map((issue) => ({
      pageNumber: issue.pageNumber,
      issueType: issue.issueType,
      details: issue.details,
    }));

    const description = JSON.stringify(formattedIssues);

    const fn =
      job && job.id
        ? rateJob({
            jobId: job.id,
            rating: 1,
            details: description,
          })
        : rateJobByDocumentId({
            documentId: docType === "bank" ? selectedDocumentId : documentId,
            rating: 1,
            details: description,
          });

    fn.then(() => {
      setLoading(false);
      dispatch(
        createAlert({
          message: "We're looking at the issue, thank you for your feedback",
          timeout: 0,
          type: "success",
        })
      );
      resetForm();
      if (job && job.id) {
        dispatch(fetchJobRatings(job.id));
      }
    }).catch((err) => {
      console.error(err);
      setLoading(false);
      dispatch(
        createAlert({
          message: "Error reporting issue",
          timeout: 0,
          type: "error",
        })
      );
    });
  };

  const validationSchema = Yup.object().shape({
    issues: Yup.array()
      .of(
        Yup.object().shape({
          pageNumber: Yup.string(),
          issueType: Yup.string().required("Please select an issue type"),
          details: Yup.string(),
        })
      )
      .min(1, "Please add at least one issue to report"),
  });

  const issueTypes =
    docType === "bank"
      ? bankStatementIssueTypes
      : docType === "invoice"
      ? invoiceIssueTypes
      : docType === "form"
      ? formIssueTypes
      : genericIssueTypes;

  return (
    <>
      <AlertBanner />
      <div className="m-2">{description}</div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, handleSubmit, errors, touched }) => {
          const addIssue = () => {
            if (values.issueType) {
              const newIssue = {
                pageNumber: values.pageNumber || "",
                issueType: values.issueType,
                details: values.details || "",
              };
              setFieldValue("issues", [...values.issues, newIssue]);
              // Reset all form values
              setFieldValue("pageNumber", "");
              setFieldValue("issueType", "");
              setFieldValue("details", "");
              // Reset touched state for all fields
              setFieldValue("touched", {
                pageNumber: false,
                issueType: false,
                details: false,
              });
              // Reset Select value to null
              setFieldValue("issueType", null);
            }
          };

          const removeIssue = (index: number) => {
            const newIssues = [...values.issues];
            newIssues.splice(index, 1);
            setFieldValue("issues", newIssues);
          };

          return (
            <Form>
              <div className="mb-3">
                <h5>Report New Issue</h5>
                <Row>
                  <Form.Group className="mb-2 col-md-6">
                    <Form.Label>Issue Type</Form.Label>
                    <Select
                      options={issueTypes}
                      value={issueTypes.find(
                        (type) => type.value === values.issueType
                      )}
                      onChange={(option) =>
                        setFieldValue("issueType", option?.value)
                      }
                      onBlur={() => setFieldValue("touched.issueType", true)}
                      placeholder="Select issue type"
                      className={
                        touched.issueType && errors.issueType
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {touched.issueType && errors.issueType && (
                      <div className="invalid-feedback d-block">
                        {errors.issueType}
                      </div>
                    )}
                  </Form.Group>

                  {[
                    "missing_transaction",
                    "wrong_transaction_date",
                    "wrong_transaction_amount",
                    "wrong_transaction_description",
                    "wrong_transaction_balance",
                  ].includes(values.issueType) && (
                    <Form.Group className="mb-2 col-md-6">
                      <Form.Label>Page Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.pageNumber}
                        onChange={(e) =>
                          setFieldValue("pageNumber", e.target.value)
                        }
                        onBlur={() => setFieldValue("touched.pageNumber", true)}
                        placeholder="Enter page number"
                        isInvalid={touched.pageNumber && !!errors.pageNumber}
                        className={
                          touched.pageNumber && errors.pageNumber
                            ? "is-invalid"
                            : ""
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.pageNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}
                </Row>

                <Form.Group className="mb-2">
                  <Form.Label>Details (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={values.details}
                    onChange={(e) => setFieldValue("details", e.target.value)}
                    onBlur={() => setFieldValue("touched.details", true)}
                    placeholder="Describe the issue in detail (optional)"
                  />
                </Form.Group>

                <Button
                  variant="secondary"
                  onClick={addIssue}
                  disabled={
                    !values.issueType ||
                    ([
                      "missing_transaction",
                      "wrong_transaction_date",
                      "wrong_transaction_amount",
                      "wrong_transaction_description",
                      "wrong_transaction_balance",
                    ].includes(values.issueType) &&
                      !values.pageNumber)
                  }
                >
                  Add Issue
                </Button>
              </div>

              {values.issues.length > 0 && (
                <div className="mb-3">
                  <h5>Reported Issues</h5>
                  {values.issues.map((issue, index) => (
                    <div key={index} className="card mb-2">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            {issue.pageNumber && (
                              <strong>Page {issue.pageNumber}</strong>
                            )}
                            {issue.pageNumber && " - "}
                            {
                              issueTypes.find(
                                (type) => type.value === issue.issueType
                              )?.label
                            }
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeIssue(index)}
                          >
                            Remove
                          </Button>
                        </div>
                        <div className="mt-2">{issue.details}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button
                variant="primary"
                onClick={() => handleSubmit()}
                disabled={loading || values.issues.length === 0}
              >
                {loading ? <ClipLoader size={20} color="#fff" /> : cta}
              </Button>
              {values.issues.length === 0 && (
                <div className="text-muted mt-2">
                  Please add at least one issue to report before submitting
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export const JobHelpMenu: React.FC<Props> = ({}) => {
  const options = [
    {
      label: "The job didn't reconcile",
      value: "reconciliation",
      type: "qbo",
    },
    {
      label: "Dates are wrong",
      value: "dates",
      type: "qbo",
    },
    {
      label: "A few transactions are wrong/missing",
      value: "fewMissingTransactions",
      type: "qbo",
    },
    {
      label:
        "Many transactions are wrong/missing or full pages/sections are missing",
      value: "pageMissingTransactions",
      type: "qbo",
    },
    {
      label: "There are lots of red lines",
      value: "redLines",
      type: "qbo",
    },
    {
      label: "Issues importing into QuickBooks",
      value: "quickbooks",
      type: "qbo",
    },
    {
      label: "This job only supports XLSX downloads",
      value: "onlyXlsx",
      type: "generic",
    },
    {
      label: "The spreadsheet has extra data, I only want the transactions",
      value: "extraData",
      type: "generic",
    },
    {
      label: "Other issues",
      value: "other",
      type: "both",
    },
  ];

  const { job } = useSelector((state: ReduxState) => state.JobData);

  const validOptions = options.filter((o) => {
    if (o.type === "both") {
      return true;
    }
    if (job && job.isGeneric) {
      return o.type === "generic";
    } else if (job && !job.isGeneric) {
      return o.type === "qbo";
    }
    return false;
  });

  const [issueType, setIssueType] = React.useState<{
    value: string;
    label: string;
    type: string;
  }>(validOptions[0]);

  return (
    <div>
      <Select
        options={validOptions}
        value={issueType}
        onChange={(value) => {
          if (value) {
            setIssueType(value);
          }
        }}
      />

      <div className="m-2">
        {issueType.value === "reconciliation" && (
          <div className="m-2">
            <a
              href="https://www.docuclipper.com/docs/ui/user-guide/resolving-issues/reconciliation/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon="question-circle" />
              <span className="ml-1">
                Documentation on how to reconcile the statements.
              </span>
              <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
            </a>
          </div>
        )}

        {issueType.value === "dates" && (
          <div className="m-2">
            <a
              href="https://www.docuclipper.com/docs/ui/user-guide/resolving-issues/dates/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon="question-circle" />
              <span className="ml-1">Documentation on how to fix dates.</span>
              <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
            </a>
          </div>
        )}
        {issueType.value === "pageMissingTransactions" && (
          <div>
            <DescribeIssue
              cta="Report Issue"
              description={`Describe the issue. Include details like file names, page numbers and as
        much information as possible to help the support team triage and fix the
        problem.`}
            />
          </div>
        )}
        {issueType.value === "fewMissingTransactions" && (
          <div className="m-2">
            <a
              href="https://www.docuclipper.com/docs/ui/user-guide/resolving-issues/reconciliation/#fixing-errors-to-reconcile-the-statement"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon="question-circle" />
              <span className="ml-1">
                Documentation on how to fix the transactions.
              </span>
              <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
            </a>
          </div>
        )}
        {issueType.value === "redLines" && (
          <div className="m-2">
            <a
              href="https://www.docuclipper.com/docs/ui/user-guide/resolving-issues/reconciliation/#fixing-errors-to-reconcile-the-statement"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon="question-circle" />
              <span className="ml-1">
                Documentation on how to fix the red lines.
              </span>
              <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
            </a>
          </div>
        )}

        {issueType.value === "quickbooks" && (
          <>
            <div className="m-2">
              <a
                href="https://www.docuclipper.com/docs/ui/user-guide/extracting-data/import-data-quickbooks-desktop/#handling-import-errors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon="question-circle" />
                <span className="ml-1">
                  Documentation on how to fix QuickBooks import errors.
                </span>
                <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
              </a>
            </div>
          </>
        )}

        {issueType.value === "onlyXlsx" && (
          <>
            <div className="m-2">
              <a
                href="https://www.docuclipper.com/docs/ui/user-guide/extracting-data/generic-conversion/#generic-conversion"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon="question-circle" />
                <span className="ml-1">
                  How to fix "This job only supports XLSX downloads".
                </span>
                <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
              </a>
            </div>
          </>
        )}

        {issueType.value === "extraData" && (
          <>
            <div className="m-2">
              <a
                href="https://www.docuclipper.com/docs/ui/user-guide/extracting-data/generic-conversion/#generic-conversion"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon="question-circle" />
                <span className="ml-1">
                  How to control what data gets extracted.
                </span>
                <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
              </a>
            </div>
          </>
        )}

        {issueType.value === "other" && (
          <div>
            <DescribeIssue
              cta="Report Issue"
              description={`Describe the issue in as much detail as possible`}
            />
          </div>
        )}
      </div>
    </div>
  );
};
